import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Send, GripVertical, Mail, AlertTriangle, ChevronDown, ChevronRight, Search, Server, CheckCheck } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

const applyVars = (text, row) => (text || '')
  .replace(/\{\{cliente_nombre\}\}/g, row.nombre_padron || row.cuit || '')
  .replace(/\{\{numero_referencia\}\}/g, row.titulo || '')
  .replace(/\{\{cuit\}\}/g, row.cuit || '');

const SortableChip = ({ id, value }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      data-testid={`email-chip-${id}`}
      className="flex items-center gap-1.5 bg-white border border-slate-300 rounded-full px-3 py-1 text-xs font-mono cursor-grab active:cursor-grabbing shadow-sm select-none">
      <GripVertical size={12} className="text-slate-400" />
      {value}
    </div>
  );
};

const EmailBulkDialog = ({ open, onOpenChange, caseIds, api, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaId, setPlantillaId] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [chips, setChips] = useState([]); // [{id, value}]
  const [expanded, setExpanded] = useState({}); // case_id -> bool
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [buscandoWaya, setBuscandoWaya] = useState(false);
  const [wayaProgress, setWayaProgress] = useState(null);
  const [rotacion, setRotacion] = useState(null);
  const [omitirYaEnviados, setOmitirYaEnviados] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    if (!open || !caseIds?.length) return;
    setLoading(true);
    setPlantillaId(''); setPasteText(''); setChips([]); setExpanded({}); setOmitirYaEnviados(false);
    Promise.all([
      api.post('/cases/email-preview-bulk', { case_ids: caseIds }),
      api.get('/solicitudes-config/plantillas').catch(() => ({ data: [] })),
      api.get('/notifications/rotacion').catch(() => ({ data: null })),
    ]).then(([prevRes, pltRes, rotRes]) => {
      setRows((prevRes.data.items || []).map(r => ({ ...r, manual: false })));
      setPlantillas(pltRes.data || []);
      setRotacion(rotRes.data || null);
    }).catch(() => toast.error('No se pudo cargar la previsualización'))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlantillaChange = (id) => {
    setPlantillaId(id);
    const plt = plantillas.find(p => p.id === id);
    if (!plt) return;
    setRows(rs => rs.map(r => r.manual ? r : ({
      ...r,
      asunto: applyVars(plt.asunto, r) || r.asunto,
      contenido: applyVars(plt.contenido, r),
    })));
  };

  const detectarEmails = () => {
    const found = (pasteText.match(EMAIL_RE) || []).map(e => e.trim().toLowerCase());
    const unique = [...new Set(found)];
    setChips(unique.map((value, i) => ({ id: `chip-${i}-${value}`, value })));
    if (unique.length === 0) toast.warning('No se detectaron emails válidos en el texto');
    else toast.success(`${unique.length} email(s) detectado(s)${found.length !== unique.length ? ` (${found.length - unique.length} duplicado(s) eliminado(s))` : ''}`);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setChips(items => {
        const oldI = items.findIndex(i => i.id === active.id);
        const newI = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldI, newI);
      });
    }
  };

  const asignarEnOrden = () => {
    const sinMailIdx = rows.map((r, i) => (!r.to_email ? i : -1)).filter(i => i >= 0);
    if (sinMailIdx.length === 0) { toast.info('Todos los casos ya tienen email'); return; }
    if (chips.length === 0) { toast.warning('Primero detectá los emails'); return; }
    setRows(rs => {
      const copy = [...rs];
      sinMailIdx.forEach((rowI, k) => {
        if (chips[k]) copy[rowI] = { ...copy[rowI], to_email: chips[k].value };
      });
      return copy;
    });
    const n = Math.min(sinMailIdx.length, chips.length);
    toast.success(`${n} email(s) asignado(s) en orden`);
  };

  const updateRow = (caseId, patch) => {
    setRows(rs => rs.map(r => r.case_id === caseId ? { ...r, ...patch, manual: true } : r));
  };

  const buscarEnWaya = async () => {
    const sinMail = rows.filter(r => !r.to_email).map(r => r.case_id);
    if (sinMail.length === 0) { toast.info('Todos los casos ya tienen email'); return; }
    setBuscandoWaya(true);
    setWayaProgress({ procesados: 0, total: sinMail.length });
    try {
      const res = await api.post('/cases/buscar-emails-waya-bulk', { case_ids: sinMail });
      const jobId = res.data?.job_id;
      let job = null;
      for (let i = 0; i < 600; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const jr = await api.get(`/cases/buscar-emails-waya-job/${jobId}`);
        job = jr.data;
        setWayaProgress({ procesados: job?.procesados || 0, total: job?.total || sinMail.length });
        // aplicar en vivo los emails encontrados hasta el momento
        const parcial = (job?.parcial || []).filter(it => it.to_email);
        if (parcial.length > 0) {
          const map = Object.fromEntries(parcial.map(it => [it.case_id, it.to_email]));
          setRows(rs => rs.map(r => (map[r.case_id] && !r.to_email) ? { ...r, to_email: map[r.case_id] } : r));
        }
        if (job?.estado === 'completado') break;
      }
      const found = (job?.resultado?.items || job?.parcial || []).filter(it => it.to_email);
      const errs = job?.resultado?.errores_waya || [];
      if (found.length > 0) {
        const map = Object.fromEntries(found.map(it => [it.case_id, it.to_email]));
        setRows(rs => rs.map(r => map[r.case_id] ? { ...r, to_email: map[r.case_id] } : r));
        toast.success(`${found.length} email(s) encontrado(s) en Waya`);
      } else if (errs.length > 0) {
        toast.error(`Waya devolvió un error: ${errs[0]}`);
      } else {
        toast.warning('No se encontraron emails en Waya para los casos sin mail');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al buscar en Waya');
    } finally {
      setBuscandoWaya(false);
      setWayaProgress(null);
    }
  };

  const sinMailCount = rows.filter(r => !r.to_email).length;
  const listosCount = rows.filter(r => r.to_email).length;
  const yaEnviadosCount = rows.filter(r => r.to_email && r.ya_enviado).length;

  const fmtFecha = (s) => {
    if (!s) return '';
    try { return new Date(s).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: '2-digit' }); }
    catch { return s; }
  };

  const enviar = async () => {
    let conMail = rows.filter(r => r.to_email);
    if (omitirYaEnviados) conMail = conMail.filter(r => !r.ya_enviado);
    if (conMail.length === 0) {
      toast.error(omitirYaEnviados && yaEnviadosCount > 0
        ? 'Todos los casos con email ya fueron notificados (y elegiste omitirlos)'
        : 'Ningún caso tiene email asignado');
      return;
    }
    const dupCount = conMail.filter(r => r.ya_enviado).length;
    if (dupCount > 0 && !window.confirm(`⚠️ ${dupCount} de los ${conMail.length} caso(s) YA recibieron un email previamente. Si continuás, recibirán un email DUPLICADO.\n\n¿Enviar de todas formas?`)) return;
    const omitidos = rows.filter(r => r.to_email).length - conMail.length;
    if (sinMailCount > 0 && !window.confirm(`${sinMailCount} caso(s) quedaron SIN email y NO se enviarán.${omitidos > 0 ? ` ${omitidos} ya notificado(s) serán omitidos.` : ''} ¿Continuar y enviar los ${conMail.length} con email?`)) return;
    setSending(true);
    setProgress({ procesados: 0, total: conMail.length });
    try {
      const items = conMail.map(r => ({ case_id: r.case_id, email_override: r.to_email, asunto: r.asunto, contenido: r.contenido }));
      const res = await api.post('/cases/enviar-email-bulk', { items });
      const jobId = res.data?.job_id;
      let job = null;
      for (let i = 0; i < 480; i++) {
        await new Promise(r => setTimeout(r, 2500));
        const jr = await api.get(`/cases/enviar-email-job/${jobId}`);
        job = jr.data;
        setProgress({ procesados: job?.procesados || 0, total: job?.total || conMail.length });
        if (job?.estado === 'completado') break;
      }
      onComplete?.(job?.resultado || {}, items);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar');
    } finally {
      setSending(false);
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="email-bulk-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail size={18} className="text-teal-600" /> Enviar email a {rows.length} caso(s)</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Cargando previsualización…</div>
        ) : (
          <div className="space-y-4">
            {/* Cuenta de envío (rotación) */}
            {rotacion && rotacion.activas > 0 && (
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 flex items-start gap-2" data-testid="email-bulk-rotacion-banner">
                <Server size={16} className="text-teal-600 mt-0.5" />
                <div className="text-xs text-teal-800 leading-relaxed">
                  Los correos saldrán desde{' '}
                  <strong className="font-mono" data-testid="email-bulk-cuenta-actual">{rotacion.cuenta_actual || '—'}</strong>
                  {' '}y rotarán automáticamente al llegar a {rotacion.limite}/cuenta.
                  <span className="ml-1">Capacidad restante hoy: <strong>{rotacion.capacidad_restante}</strong> correo(s) en {rotacion.activas} cuenta(s) activa(s).</span>
                  {listosCount > rotacion.capacidad_restante && (
                    <span className="block mt-1 text-amber-700 font-medium">⚠️ Querés enviar {listosCount} pero solo hay capacidad para {rotacion.capacidad_restante} hoy. El resto fallará por límite.</span>
                  )}
                </div>
              </div>
            )}

            {/* Plantilla */}
            <div>
              <label className="text-xs text-slate-500 uppercase font-medium">Plantilla (se aplica a todos)</label>
              <select value={plantillaId} onChange={e => handlePlantillaChange(e.target.value)}
                data-testid="email-bulk-plantilla-select"
                className="w-full h-9 text-sm rounded border border-slate-200 px-2 mt-1 bg-white">
                <option value="">Usar texto armado por caso…</option>
                {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>

            {/* Lista de mails para casos sin email */}
            {sinMailCount > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="email-bulk-paste-section">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                  <AlertTriangle size={15} /> {sinMailCount} caso(s) sin email detectado
                </div>
                <p className="text-xs text-amber-700">Pegá una lista de mails: detecto los válidos, elimino duplicados y podés <strong>arrastrarlos para ordenarlos</strong>. Luego "Asignar en orden" los reparte a los casos sin mail (en el orden de la tabla).</p>
                <Textarea rows={3} value={pasteText} onChange={e => setPasteText(e.target.value)}
                  placeholder={'juan@mail.com, ana@mail.com\npedro@empresa.com'}
                  className="font-mono text-xs bg-white" data-testid="email-bulk-paste-textarea" />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={buscarEnWaya} disabled={buscandoWaya}
                    data-testid="email-bulk-buscar-waya-btn" className="border-cyan-300 text-cyan-700 hover:bg-cyan-50">
                    <Search size={13} className="mr-1" />
                    {buscandoWaya ? (wayaProgress ? `Buscando en Waya ${wayaProgress.procesados}/${wayaProgress.total}…` : 'Buscando…') : `Buscar en Waya (${sinMailCount})`}
                  </Button>
                  <Button size="sm" variant="outline" onClick={detectarEmails} data-testid="email-bulk-detect-btn">
                    <Search size={13} className="mr-1" /> Detectar emails
                  </Button>
                  <Button size="sm" onClick={asignarEnOrden} disabled={chips.length === 0}
                    className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="email-bulk-asignar-btn">
                    Asignar en orden ({Math.min(chips.length, sinMailCount)})
                  </Button>
                </div>
                <p className="text-[11px] text-amber-700">Tip: "Buscar en Waya" consulta el portal por cada caso (puede tardar) y completa los que encuentre. Si preferís, pegá la lista manualmente.</p>
                {chips.length > 0 && (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={chips.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                      <div className="flex flex-wrap gap-2 pt-1" data-testid="email-bulk-chips">
                        {chips.map(c => <SortableChip key={c.id} id={c.id} value={c.value} />)}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            )}

            {/* Tabla de casos */}
            <div className="rounded-md border border-slate-200 overflow-hidden">
              <div className="grid grid-cols-12 bg-slate-50 text-[11px] uppercase font-semibold text-slate-500 px-3 py-2">
                <div className="col-span-4">Caso / CUIT</div>
                <div className="col-span-5">Email destinatario</div>
                <div className="col-span-3">Estado</div>
              </div>
              <div className="max-h-[40vh] overflow-y-auto divide-y divide-slate-100">
                {rows.map((r, idx) => (
                  <div key={r.case_id} data-testid={`email-bulk-caserow-${idx}`} className="px-3 py-2">
                    <div className="grid grid-cols-12 items-center gap-2">
                      <div className="col-span-4 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{r.nombre_padron || r.titulo}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{r.cuit || '-'}</div>
                      </div>
                      <div className="col-span-5">
                        <Input value={r.to_email} onChange={e => updateRow(r.case_id, { to_email: e.target.value })}
                          placeholder="sin email — pegá la lista arriba" data-testid={`email-bulk-emailinput-${idx}`}
                          className={`h-8 text-xs font-mono ${!r.to_email ? 'border-amber-300 bg-amber-50' : ''}`} />
                      </div>
                      <div className="col-span-3 flex items-center gap-2">
                        {r.to_email
                          ? (r.tiene_datos
                            ? <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-emerald-100 text-emerald-700">Con datos</span>
                            : <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-slate-200 text-slate-700">Plano</span>)
                          : <span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-100 text-amber-700">Sin mail</span>}
                        {r.ya_enviado && (
                          <span title={`Último envío: ${fmtFecha(r.ultimo_envio?.fecha)}${r.ultimo_envio?.destinatario ? ' → ' + r.ultimo_envio.destinatario : ''}`}
                            data-testid={`email-bulk-yaenviado-${idx}`}
                            className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-orange-100 text-orange-700 flex items-center gap-0.5">
                            <CheckCheck size={11} /> Ya enviado {fmtFecha(r.ultimo_envio?.fecha)}
                          </span>
                        )}
                        <button onClick={() => setExpanded(e => ({ ...e, [r.case_id]: !e[r.case_id] }))}
                          data-testid={`email-bulk-expand-${idx}`}
                          className="text-slate-400 hover:text-slate-600">
                          {expanded[r.case_id] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                        </button>
                      </div>
                    </div>
                    {expanded[r.case_id] && (
                      <div className="mt-2 space-y-2 pl-1">
                        <Input value={r.asunto} onChange={e => updateRow(r.case_id, { asunto: e.target.value })}
                          placeholder="Asunto" data-testid={`email-bulk-asunto-${idx}`} className="h-8 text-xs" />
                        <Textarea rows={4} value={r.contenido} onChange={e => updateRow(r.case_id, { contenido: e.target.value })}
                          data-testid={`email-bulk-contenido-${idx}`} className="text-xs" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-md bg-emerald-100 text-emerald-800 px-2 py-1 font-medium">Listos: {listosCount}</span>
              <span className="rounded-md bg-amber-100 text-amber-800 px-2 py-1 font-medium">Sin mail: {sinMailCount}</span>
              {yaEnviadosCount > 0 && (
                <span className="rounded-md bg-orange-100 text-orange-800 px-2 py-1 font-medium" data-testid="email-bulk-yaenviados-count">Ya notificados: {yaEnviadosCount}</span>
              )}
            </div>
            {yaEnviadosCount > 0 && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 space-y-2" data-testid="email-bulk-duplicados-section">
                <div className="flex items-center gap-2 text-sm font-medium text-orange-800">
                  <CheckCheck size={15} /> {yaEnviadosCount} caso(s) ya recibieron un email antes
                </div>
                <p className="text-xs text-orange-700">Para evitar duplicados podés omitirlos. Si los dejás incluidos, te pediré confirmación antes de reenviarles.</p>
                <label className="flex items-center gap-2 text-xs text-orange-800 cursor-pointer select-none" data-testid="email-bulk-omitir-label">
                  <input type="checkbox" checked={omitirYaEnviados} onChange={e => setOmitirYaEnviados(e.target.checked)}
                    data-testid="email-bulk-omitir-checkbox" className="rounded border-orange-400 text-orange-600" />
                  Omitir los {yaEnviadosCount} casos ya notificados (no reenviar)
                </label>
              </div>
            )}
            <p className="text-[10px] text-slate-400">Cada envío queda registrado en el timeline de su caso. Los casos sin mail no se envían.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={sending || loading || (omitirYaEnviados ? (listosCount - yaEnviadosCount) === 0 : listosCount === 0)}
            data-testid="email-bulk-send-confirm-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
            <Send size={14} className="mr-1" />
            {sending ? (progress ? `Enviando ${progress.procesados}/${progress.total}…` : 'Enviando…') : `Enviar (${omitirYaEnviados ? listosCount - yaEnviadosCount : listosCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailBulkDialog;
